(() => {

  const getVariantCardMax = (form) => {
    if (!(form instanceof HTMLFormElement)) return null;
    const rawMax = form.dataset.variantMax;
    if (rawMax === undefined || rawMax === null || rawMax === '') return null;

    const maxValue = Number(rawMax);
    if (!Number.isFinite(maxValue)) return null;

    const cartQty = Number(form.dataset.variantCart || 0);
    const remaining = maxValue - cartQty;
    return remaining > 0 ? remaining : 0;
  };

  const updateVariantCardPriceState = (form, isSoldOut) => {
    if (!(form instanceof HTMLFormElement)) return;

    const card = form.closest('.variant-card');
    if (!(card instanceof HTMLElement)) return;

    const priceBox = card.querySelector('.variant-card__price-box');
    if (!(priceBox instanceof HTMLElement)) return;

    if (!priceBox.dataset.defaultPriceHtml) {
      priceBox.dataset.defaultPriceHtml = priceBox.innerHTML;
    }

    if (isSoldOut) {
      priceBox.innerHTML = '<p class="variant-card__sold-out">SOLD OUT</p>';
      return;
    }

    if (priceBox.dataset.defaultPriceHtml) {
      priceBox.innerHTML = priceBox.dataset.defaultPriceHtml;
    }
  };

  const updateVariantCardButtonState = (form, button, max) => {
    if (!(button instanceof HTMLButtonElement)) return;

    if (max !== null && max <= 0) {
      updateVariantCardPriceState(form, true);
      if (button.dataset.defaultText) {
        button.textContent = button.dataset.defaultText;
      }
      button.disabled = true;
      button.classList.add('variant-card__button--disabled');
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    updateVariantCardPriceState(form, false);

    if (button.dataset.defaultText) {
      button.textContent = button.dataset.defaultText;
    }
    button.disabled = false;
    button.classList.remove('variant-card__button--disabled');
    button.removeAttribute('aria-disabled');
  };

  const submitVariantCardForm = async (form) => {
    if (!(form instanceof HTMLFormElement)) return;

    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (!button.dataset.defaultText) {
      button.dataset.defaultText = button.textContent || 'ADD TO CART';
    }

    const maxForCard = getVariantCardMax(form);
    if (maxForCard !== null && maxForCard <= 0) {
      updateVariantCardButtonState(form, button, maxForCard);
      return;
    }

    const formData = new FormData(form);
    let quantity = Number(formData.get('quantity')) || 1;
    if (maxForCard !== null) {
      quantity = Math.min(quantity, maxForCard);
      formData.set('quantity', String(quantity));
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    if (sectionIds.length > 0) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const previousText = button.textContent;
    button.disabled = true;
    button.classList.add('variant-card__button--loading');
    button.textContent = 'ADDING...';

    try {
      const response = await fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });

      const json = await response.json();

      if (!response.ok || json.status) {
        throw new Error(json.description || json.message || 'Failed to add item to cart');
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: json,
            sourceId: 'variant-card',
            data: {
              source: 'product-form-component',
              itemCount: quantity,
              variantId: String(formData.get('id') || ''),
              sections: json.sections,
            },
          },
        })
      );

      if (maxForCard !== null) {
        const currentCartQty = Number(form.dataset.variantCart || 0);
        form.dataset.variantCart = String(currentCartQty + quantity);
        updateVariantCardButtonState(form, button, getVariantCardMax(form));
      }

      button.textContent = 'ADDED';
      window.setTimeout(() => {
        updateVariantCardButtonState(form, button, getVariantCardMax(form));
        if (getVariantCardMax(form) === null) {
          button.textContent = previousText;
        }
      }, 900);
    } catch (error) {
      console.error(error);
      button.textContent = 'ERROR';
      window.setTimeout(() => {
        updateVariantCardButtonState(form, button, getVariantCardMax(form));
        if (getVariantCardMax(form) === null) {
          button.textContent = previousText;
        }
      }, 1200);
    } finally {
      if (maxForCard === null) {
        button.disabled = false;
      }
      button.classList.remove('variant-card__button--loading');
    }
  };

  const setupVariantCardForms = () => {
    const forms = document.querySelectorAll('.variant-card__form');

    for (const form of forms) {
      if (!(form instanceof HTMLFormElement) || form.dataset.initialized === 'true') continue;

      const button = form.querySelector('button[type="submit"]');
      if (button instanceof HTMLButtonElement && !button.dataset.defaultText) {
        button.dataset.defaultText = button.textContent || 'ADD TO CART';
      }

      if (button instanceof HTMLButtonElement) {
        updateVariantCardButtonState(form, button, getVariantCardMax(form));
      }

      form.dataset.initialized = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitVariantCardForm(form);
      });
    }
  };

  const setupIssuesCollection = () => {
    const issueCollections = document.querySelectorAll('[data-issues-collection]');

    for (const issueCollection of issueCollections) {
      if (!(issueCollection instanceof HTMLElement) || issueCollection.dataset.initialized === 'true') continue;

      const issuesScroller = issueCollection.querySelector('.issues-collection__scroller');

      const cards = Array.from(issueCollection.querySelectorAll('[data-issue-card]'));
      const tabs = Array.from(issueCollection.querySelectorAll('[data-issue-tab]'));
      const contents = Array.from(issueCollection.querySelectorAll('[data-issue-content]'));

      if (cards.length === 0 || tabs.length === 0 || contents.length === 0) {
        continue;
      }

      const runtimeIssuesCount = cards.length;
      if (issuesScroller instanceof HTMLElement) {
        issuesScroller.style.setProperty('--issues-count-runtime', String(runtimeIssuesCount));
      }
      issueCollection.classList.toggle('issues-collection--featured-solo', runtimeIssuesCount > 2);

      issueCollection.classList.toggle('issues-collection--tabs-compact', tabs.length > 3);
      document.body.classList.remove('issues-page-detail-open');

      const normalizeIssueHandle = (value) => {
        if (!value) return '';
        return value.trim().toLowerCase().normalize('NFKC');
      };

      const scrollToPageTop = (behavior) => {
        window.scrollTo({ top: 0, behavior });
      };

      const setupIssueCoverHover = () => {
        if (!window.matchMedia('(hover: hover)').matches) return;

        for (const card of cards) {
          const coversWrapper = card.querySelector('[data-issue-covers]');
          if (!(coversWrapper instanceof HTMLElement)) continue;
          if (coversWrapper.dataset.hoverBound === 'true') continue;

          const clearActive = () => coversWrapper.classList.remove('issues-covers--active');
          const showSpread = () => coversWrapper.classList.add('issues-covers--active');
          const hoverTarget = card.classList.contains('issues-card--featured') ? coversWrapper : card;

          hoverTarget.addEventListener('pointerenter', showSpread);
          hoverTarget.addEventListener('pointerleave', clearActive);
          hoverTarget.addEventListener('pointercancel', clearActive);

          coversWrapper.dataset.hoverBound = 'true';
        }
      };

      const selectIssue = (
        card,
        { scrollBehavior = 'smooth', updateHistory = true, scrollCard = true, scrollDetail = true } = {}
      ) => {
        issueCollection.classList.add('issues-collection--issue-selected');
        issueCollection.classList.add('issues-collection--tabs-visible');
        document.body.classList.add('issues-page-detail-open');

        const issueTarget = card.dataset.issueTarget || '';
        const issueHandle = card.dataset.issueHandle || '';

        for (const item of cards) {
          const isActive = item === card;
          item.classList.toggle('issues-card--active', isActive);
          item.setAttribute('aria-pressed', isActive ? 'true' : 'false');

          const caption = item.querySelector('[data-issue-caption]');
          if (caption instanceof HTMLElement) {
            caption.hidden = true;
          }
        }

        let activeTab = null;
        for (const tab of tabs) {
          const isActive = tab.dataset.issueTarget === issueTarget;
          tab.classList.toggle('issues-tab--active', isActive);
          tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          if (isActive) activeTab = tab;
        }

        for (const content of contents) {
          const isActive = content.id === issueTarget;
          content.classList.toggle('issues-reader__issue--active', isActive);
          content.hidden = !isActive;
        }

        if (updateHistory && issueHandle) {
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('issue', issueHandle);
          const nextSearch = nextUrl.searchParams.toString();
          window.history.replaceState({}, '', nextSearch ? `${nextUrl.pathname}?${nextSearch}` : nextUrl.pathname);
        }

        document.dispatchEvent(new CustomEvent('magazine:refresh'));
        if (scrollCard) {
          card.scrollIntoView({ behavior: scrollBehavior, block: 'nearest', inline: 'center' });
        }
        if (scrollDetail) {
          scrollToPageTop(scrollBehavior);
        }
      };

      const selectIssueByTarget = (issueTarget, options = {}) => {
        const card = cards.find((item) => item.dataset.issueTarget === issueTarget);
        if (card instanceof HTMLElement) {
          selectIssue(card, options);
          return true;
        }

        return false;
      };

      const selectIssueByHandle = (issueHandle, options = {}) => {
        const normalizedIssueHandle = normalizeIssueHandle(issueHandle);
        const card = cards.find((item) => normalizeIssueHandle(item.dataset.issueHandle || '') === normalizedIssueHandle);
        if (card instanceof HTMLElement) {
          selectIssue(card, options);
          return true;
        }

        return false;
      };

      issueCollection.addEventListener('click', (event) => {
        const card = event.target instanceof Element ? event.target.closest('[data-issue-card]') : null;
        if (!(card instanceof HTMLElement)) return;

        if (
          card instanceof HTMLAnchorElement &&
          event instanceof MouseEvent &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey
        ) {
          event.preventDefault();
        }

        selectIssue(card);
      });

      issueCollection.addEventListener('click', (event) => {
        const tab = event.target instanceof Element ? event.target.closest('[data-issue-tab]') : null;
        if (!(tab instanceof HTMLElement)) return;

        const issueTarget = tab.dataset.issueTarget;
        if (!issueTarget) return;
        selectIssueByTarget(issueTarget);
      });

      issueCollection.addEventListener('click', (event) => {
        const navLink = event.target instanceof Element
          ? event.target.closest('.magazine-page__bottom-nav-link--highlight')
          : null;
        if (!(navLink instanceof HTMLAnchorElement)) return;

        if (
          event instanceof MouseEvent &&
          (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        ) {
          return;
        }

        const targetUrl = new URL(navLink.href, window.location.href);
        const issueHandle = targetUrl.searchParams.get('issue');
        if (!issueHandle) return;

        event.preventDefault();
        selectIssueByHandle(issueHandle, { scrollBehavior: 'smooth', scrollCard: false, scrollDetail: true });
      });

      const params = new URL(window.location.href).searchParams;
      const issueFromUrl = params.get('issue');
      if (issueFromUrl) {
        if (!selectIssueByHandle(issueFromUrl, { scrollBehavior: 'smooth', updateHistory: false })) {
          selectIssueByHandle(decodeURIComponent(issueFromUrl), { scrollBehavior: 'smooth', updateHistory: false });
        }
      }

      setupIssueCoverHover();

      // --- REORDER, CAROUSEL & PULSE --- //

      const reorderCards = () => {
        if (!(issuesScroller instanceof HTMLElement)) return;
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const key = isMobile ? 'orderMobile' : 'orderDesktop';

        const sorted = [...cards].sort((a, b) => {
          const aVal = parseInt(a.dataset[key] || '', 10);
          const bVal = parseInt(b.dataset[key] || '', 10);
          if (Number.isFinite(aVal) && Number.isFinite(bVal)) return aVal - bVal;
          if (Number.isFinite(aVal)) return -1;
          if (Number.isFinite(bVal)) return 1;
          return 0;
        });

        sorted.forEach((card) => issuesScroller.appendChild(card));
      };

      const applyPulse = () => {
        for (const card of cards) {
          card.classList.toggle('issues-card--pulse', card.dataset.pulseIssue === 'true');
        }
      };

      const handleBreakpointChange = () => {
        reorderCards();
        applyPulse();
      };

      const bpMedia = window.matchMedia('(max-width: 749px)');
      if (bpMedia.addEventListener) {
        bpMedia.addEventListener('change', handleBreakpointChange);
      } else {
        // Safari <14 fallback
        bpMedia.addListener(handleBreakpointChange);
      }

      reorderCards();
      applyPulse();

      issueCollection.dataset.initialized = 'true';
    }
  };

  const ajaxAddToCart = async (form, event) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (!window.Theme?.routes?.cart_add_url) return false;
    if (form.closest('product-form-component')) return false;
    if (form.dataset.ajaxCartHandled === 'true') return false;

    event?.preventDefault?.();
    form.dataset.ajaxCartHandled = 'true';

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    if (sectionIds.length > 0) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });

      const json = await response.json();
      if (!response.ok || json.status) {
        throw new Error(json.description || json.message || 'Failed to add item to cart');
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: json,
            sourceId: 'custom-ajax-cart',
            data: {
              source: 'custom-ajax-cart',
              itemCount: Number(formData.get('quantity')) || 1,
              variantId: String(formData.get('id') || ''),
              sections: json.sections,
            },
          },
        })
      );

      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      form.dataset.ajaxCartHandled = 'false';
    }
  };

  const bootIssues = () => {
    setupIssuesCollection();
  };

  const bootCart = () => {
    if (!window.Theme?.routes?.cart_add_url) return;
    setupVariantCardForms();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bootIssues();
      bootCart();
    });
  } else {
    bootIssues();
    bootCart();
  }

  document.addEventListener('shopify:section:load', () => {
    bootIssues();
    bootCart();
  });

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains('variant-card__form')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      submitVariantCardForm(form);
    },
    true
  );

})();
